/**
 * useAuth Hook
 * 
 * Custom hook that provides authentication state and methods.
 * This is a convenience wrapper around the AuthContext useAuth hook.
 * 
 * @returns {Object} Auth state and methods
 */

import { useAuth as useAuthContext } from '../context/AuthContext';

/**
 * useAuth Hook
 * 
 * Provides access to authentication state and methods from AuthContext.
 * This hook includes:
 * - User information
 * - Authentication token
 * - Authentication status
 * - Loading state
 * - Error state
 * - Auth methods (login, register, logout, verifyEmail, etc.)
 * 
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 * 
 * @example
 * const { user, isAuthenticated, login, logout } = useAuth();
 */
export const useAuth = () => {
  const auth = useAuthContext();

  return {
    // State
    user: auth.user,
    token: auth.token,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    
    // Methods
    login: auth.login,
    register: auth.register,
    logout: auth.logout,
    verifyEmail: auth.verifyEmail,
    resendVerificationEmail: auth.resendVerificationEmail,
    refreshToken: auth.refreshToken,
    updateUser: auth.updateUser,
    clearError: auth.clearError
  };
};

export default useAuth;
